<template lang="pug">
  .b-customer-spec-page
    ._error(v-if="linkError" data-testid="customer-spec-link-error") {{ linkError }}

    template(v-else-if="view")
      ._page(data-testid="customer-spec-page")
        ._head
          ._titles
            h1._title Спецификация
            span._project(data-testid="customer-spec-project-name") {{ view.projectName }}
            span._readonly(v-if="readOnly" data-testid="customer-spec-read-only")
              | проект в архиве — только чтение
          ._actions
            router-link._chats(:to="chatsRoute" data-testid="customer-spec-chats-link") Чаты
            Btn(
              v-if="!readOnly && selectedIds.length"
              blue
              data-testid="customer-spec-approve-selected"
              @click="approveSelected"
              ) Утвердить выбранные ({{ selectedIds.length }})
            Btn(
              v-else-if="!readOnly && approvableItems.length"
              blue
              data-testid="customer-spec-approve-all"
              @click="approveAll"
              ) Утвердить все

        ._summary
          ._sum
            span._sum-label Сумма для вас
            span._sum-value(data-testid="customer-spec-customer-total") {{ money(summary.customerTotal) }}
          ._sum
            span._sum-label Утверждено
            span._sum-value(data-testid="customer-spec-approved-total") {{ money(summary.approvedTotal) }}
          ._sum
            span._sum-label Экономия
            span._sum-value(data-testid="customer-spec-savings") {{ money(summary.savingsTotal) }}
          ._sum
            span._sum-label Комиссия дизайнера
            span._sum-value(data-testid="customer-spec-commission-percent") {{ percent(view.commissionPercent) }}
          ._sum
            span._sum-label Комиссия, сумма
            span._sum-value(data-testid="customer-spec-commission-total") {{ money(summary.commissionTotal) }}
          ._sum
            span._sum-label Прогресс
            span._sum-value(data-testid="customer-spec-progress")
              | Утверждено {{ summary.approvedCount || 0 }} из {{ summary.sentCount || 0 }}

        ._filters
          label._filter
            span._filter-label Помещение
            select._select(data-testid="customer-spec-filter-room" v-model="filterRoom")
              option(value="" data-testid="customer-spec-filter-room-option") Все помещения
              option(
                v-for="room in roomOptions"
                :key="room"
                :value="room"
                data-testid="customer-spec-filter-room-option"
                ) {{ room }}
          label._filter
            span._filter-label Статус
            select._select(data-testid="customer-spec-filter-status" v-model="filterStatus")
              option(value="" data-testid="customer-spec-filter-status-option") Все статусы
              option(
                v-for="status in statusOptions"
                :key="status"
                :value="status"
                data-testid="customer-spec-filter-status-option"
                ) {{ status }}

        ._empty(v-if="!groups.length") Позиций пока нет

        //- Таблица и плитки взаимоисключающие: до 768 px в разметке остаются только плитки (ТЗ 6.6)
        ._table(v-else-if="!narrow" data-testid="customer-spec-table")
          ._scroll
            ._inner(:style="{ minWidth: totalWidth + 'px' }")
              ._thead
                ._th(v-for="column in columns" :key="column.key" :style="colStyle(column.key)")
                  | {{ column.title }}
              ._group(v-for="group in groups" :key="group.key" data-testid="customer-spec-group")
                ._group-head
                  span._group-title(data-testid="customer-spec-group-title") {{ group.title }}
                  span._group-count {{ group.items.length }} позиций
                CustomerSpecRow(
                  v-for="item in group.items"
                  :key="item.id"
                  :item="item"
                  :readOnly="readOnly"
                  :selected="selectedIds.includes(item.id)"
                  @select="select(item, $event)"
                  @approve="approveOne(item)"
                  @reject="reject(item)"
                  )

        ._tiles(v-else data-testid="customer-spec-tiles")
          ._group(v-for="group in groups" :key="group.key" data-testid="customer-spec-group")
            span._group-title(data-testid="customer-spec-group-title") {{ group.title }}
            CustomerSpecRow(
              v-for="item in group.items"
              :key="item.id"
              tile
              :item="item"
              :readOnly="readOnly"
              :selected="selectedIds.includes(item.id)"
              @select="select(item, $event)"
              @approve="approveOne(item)"
              @reject="reject(item)"
              )
</template>

<script>
import CustomerSpecRow from '@/components/specification/customer/CustomerSpecRow.vue';
import CustomerRejectPopupWrappable from '@/components/specification/customer/CustomerRejectPopupWrappable.vue';
import { COLUMNS, TOTAL_WIDTH, colStyle } from '@/components/specification/customer/columns';
import model from '@/model/specCustomer';
import popup from '@/model/popup';
import { apiRun } from '@/model/apiRun';
import {
  money, percent, customerItemStatusTitle, CUSTOMER_ITEM_STATUSES,
} from '@/utils/spec';

/** Порог узкого экрана: до 768 px таблица заменяется плитками (ТЗ 6.6, 6.10). */
const NARROW_QUERY = '(max-width: 768px)';

/**
 * Спецификация заказчика (ТЗ 6.6, маршрут `/ext/spec/{идентификатор}/{подпись}`): открывается без
 * авторизации по персональной ссылке. Шапка со сводом, фильтры, таблица по категориям — на ширине до
 * 768 px вместо таблицы плитки, — утверждение выбранных или всех позиций и отклонение с причиной.
 *
 * Все числа приходят посчитанными сервером (ТЗ 4.5): страница ничего не пересчитывает. Срез полей
 * тоже задаёт сервер — плановой цены, комментария дизайнера, вознаграждения и чужих откликов в
 * ответе нет вовсе (ТЗ 3.5). В архивном проекте признак «только чтение» из ответа убирает с экрана
 * все действия (ТЗ 6.6, 6.9). Сгенерировано Claude.
 */
export default {
  name: 'CustomerSpecPage',
  components: { CustomerSpecRow },
  data() {
    return {
      view: null,
      linkError: '',
      selectedIds: [],
      filterRoom: '',
      filterStatus: '',
      narrow: false,
      media: null,
    };
  },
  computed: {
    link() { return model.linkOf(this.$route); },
    chatsRoute() {
      return { name: 'ext-spec-customer-chats', params: { ...this.link } };
    },
    columns() { return COLUMNS; },
    totalWidth() { return TOTAL_WIDTH; },
    readOnly() { return !!this.view?.readOnly; },
    summary() { return this.view?.summary ?? {}; },
    items() { return this.view?.items ?? []; },
    filteredItems() {
      return this.items.filter((item) => {
        const byRoom = !this.filterRoom || item.room === this.filterRoom;
        const byStatus = !this.filterStatus
          || customerItemStatusTitle(item.status) === this.filterStatus;
        return byRoom && byStatus;
      });
    },
    groups() { return model.groups(this.view, this.filteredItems); },
    /** «Утвердить все» — позиции «На согласовании» с учётом текущих фильтров (ТЗ 6.6). */
    approvableItems() {
      return this.filteredItems.filter((item) => item.status === 'customer_review');
    },
    roomOptions() {
      return this.items
        .map((item) => item.room)
        .filter((room) => !!room)
        .filter((room, index, list) => list.indexOf(room) === index);
    },
    /**
     * Статусы словами заказчика — только те, что действительно есть в таблице (ТЗ 6.6). Фильтр идёт
     * по слову, а не по коду: четыре статуса подбора показываются одним словом «Подбор», и выбор
     * этого слова должен оставлять все четыре.
     */
    statusOptions() {
      return Object.keys(CUSTOMER_ITEM_STATUSES)
        .filter((code) => this.items.some((item) => item.status === code))
        .map((code) => customerItemStatusTitle(code))
        .filter((title, index, list) => list.indexOf(title) === index);
    },
  },
  watch: {
    '$route.params.extId': function watchExtId() { this.load(); },
  },
  created() {
    this.load();
    this.watchWidth();
  },
  beforeDestroy() {
    if (this.media) this.media.removeEventListener('change', this.onMedia);
  },
  methods: {
    money,
    percent,
    colStyle,

    async load() {
      const { view, error } = await model.fetchSpecification(this.link);
      this.view = view ?? null;
      this.linkError = error ?? '';
      this.selectedIds = [];
    },

    /** Один и тот же порог для таблицы и для чатов заказчика (ТЗ 6.6, 6.10). */
    watchWidth() {
      this.media = window.matchMedia(NARROW_QUERY);
      this.narrow = this.media.matches;
      this.media.addEventListener('change', this.onMedia);
    },
    onMedia(event) { this.narrow = event.matches; },

    /** Ответ операции — обновлённая спецификация целиком, поэтому отметки сбрасываются. */
    apply(view) {
      this.view = view;
      this.selectedIds = [];
    },

    select(item, value) {
      this.selectedIds = value
        ? [...this.selectedIds, item.id]
        : this.selectedIds.filter((id) => id !== item.id);
    },

    approveOne(item) {
      return this.approve([item.id], `Утвердить позицию «${item.name}»?`);
    },
    approveSelected() {
      return this.approve(this.selectedIds, `Утвердить ${this.selectedIds.length} позиций?`);
    },
    approveAll() {
      const ids = this.approvableItems.map((item) => item.id);
      return this.approve(ids, `Утвердить ${ids.length} позиций?`);
    },
    /** Утверждение необратимо, поэтому с подтверждением (ТЗ 6.9). */
    async approve(itemIds, question) {
      if (!itemIds.length) return;
      const answer = await popup.addConfirm(question);
      if (!answer) return;
      const view = await apiRun(
        () => model.approve(this.link, itemIds),
        'Не удалось утвердить позиции',
      );
      if (!view) return;
      this.apply(view);
    },

    /** Отклонение выполняется из окна с формой, отдельного подтверждения не требует (ТЗ 6.9). */
    async reject(item) {
      const view = await popup.add({
        wrappable: true,
        component: CustomerRejectPopupWrappable,
        params: { link: this.link, item },
      });
      if (!view) return;
      this.apply(view);
    },
  },
};
</script>

<style scoped lang="scss">
.customer-spec-page {
  box-sizing: border-box;
  min-height: 100vh;
  padding: 24px 24px 60px;
  background: #F5F7FB;

  &__error {
    max-width: 640px;
    margin: 60px auto;
    padding: 24px;
    border-radius: 8px;
    background: var(--color-white);
    text-align: center;
    font-size: 15px;
    color: var(--color-dark-gray);
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
  }

  &__titles {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 6px 14px;
  }

  &__title {
    font-size: 28px;
    font-weight: 700;
    margin: 0;
  }

  &__project {
    font-size: 15px;
    color: var(--color-middle-gray);
  }

  &__readonly {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #E8EDF5;
    color: var(--color-middle-gray);
  }

  &__actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__chats {
    color: var(--color-accent);
    font-size: 14px;
    font-weight: 700;
  }

  &__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 28px;
    padding: 14px 16px;
    margin-bottom: 14px;
    border-radius: 8px;
    background: var(--color-white);
  }

  &__sum {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__sum-label {
    font-size: 11px;
    color: var(--color-middle-gray);
  }

  &__sum-value {
    font-size: 15px;
    font-weight: 700;
  }

  &__filters {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 14px;
  }

  &__filter {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
  }

  &__filter-label {
    color: var(--color-middle-gray);
  }

  &__select {
    font: inherit;
    font-size: 12px;
    padding: 4px 6px;
    border: 1px solid var(--color-super-light);
    border-radius: 6px;
    background: var(--color-white);
  }

  &__empty {
    padding: 30px;
    border-radius: 8px;
    background: var(--color-white);
    text-align: center;
    color: var(--color-middle-gray);
  }

  &__table {
    border-radius: 8px;
    background: var(--color-white);
    overflow: hidden;
  }

  &__scroll {
    overflow-x: auto;
  }

  &__thead {
    display: flex;
    align-items: flex-end;
    border-bottom: 1px solid var(--color-super-light);
  }

  // подписи переносятся по словам: столбцы узкие, чтобы таблица целиком помещалась на 1366 px (6.6)
  &__th {
    box-sizing: border-box;
    padding: 8px 6px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--color-middle-gray);
  }

  &__group-head {
    display: flex;
    align-items: baseline;
    gap: 14px;
    padding: 10px;
    background: #F1F5FA;
  }

  &__group-title {
    font-weight: 700;
    font-size: 14px;
  }

  &__group-count {
    font-size: 11px;
    color: var(--color-middle-gray);
  }

  &__tiles {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__tiles &__group {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  // на мониторах до 1400 px поля страницы уже: запас нужен таблице, чтобы «Поставщик» и кнопки
  // строки были видны без горизонтальной прокрутки (6.6)
  @media (max-width: 1400px) {
    padding-left: 16px;
    padding-right: 16px;
  }

  @media (max-width: 768px) {
    padding: 16px 12px 40px;

    &__title {
      font-size: 22px;
    }
  }
}
</style>
